import {
  cn,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import {
  MARITAL_STATUS_OPTIONS,
  SEX_OPTIONS,
  type QuickPlanFieldStep,
} from '@/features/client-quick-plan';
import {
  useFormContext,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { useSelector } from 'react-redux';
import { SessionType } from '@shared';
import { selectSession } from '@client/model/selectors';
import {
  STATE_AUTOFILL_OPTIONS,
  STATE_ZIP_CODES,
  stateAutofillValueForZipCode,
} from '@client/util/stateZipCodes';
import { PrefillDateField } from '@/features/invite-client/components/prefill-date-field';

const PARTNER_ELIGIBLE_STATUSES = ['Married', 'Partnered'];

interface QuickPlanPersonFieldsProps<T extends FieldValues> {
  control: Control<T>;
  fieldPrefix?: 'partner.';
  onMaritalStatusChange?: (value: string) => void;
  /**
   * Which half to render. `'all'` is the original single-step form and stays the default, so
   * every advisor outside an agent-entered program sees exactly what they saw before.
   */
  step?: QuickPlanFieldStep;
  /** Shows the email field, which only the agent-entered identity step needs. */
  showEmail?: boolean;
}

export function QuickPlanPersonFields<T extends FieldValues>({
  control,
  fieldPrefix,
  onMaritalStatusChange,
  step = 'all',
  showEmail = false,
}: QuickPlanPersonFieldsProps<T>) {
  const showIdentity = step === 'all' || step === 'identity';
  const showHealth = step === 'all' || step === 'health';

  function fieldName(name: string): FieldPath<T> {
    return (fieldPrefix ? `${fieldPrefix}${name}` : name) as FieldPath<T>;
  }

  const { sessionType } = useSelector(selectSession);
  const isWizardSession = sessionType === SessionType.wizard;
  const { setValue } = useFormContext();
  const zipValue = useWatch({ control, name: fieldName('zipCode') });
  const autofillState = stateAutofillValueForZipCode(zipValue as string | undefined);
  const autofillId = `quick-plan-${fieldPrefix ?? ''}stateZipAutofill`;

  return (
    <div className="space-y-6">
      {showIdentity && (
        <div className="grid grid-cols-2 items-start gap-4">
          <FormField
            control={control}
            name={fieldName('firstName')}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  First name <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Jane"
                    {...field}
                    value={field.value as string}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={fieldName('lastName')}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Last name <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Doe"
                    {...field}
                    value={field.value as string}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      {showIdentity && showEmail && (
        <FormField
          control={control}
          name={fieldName('email')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Client email <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="jane@example.com"
                  {...field}
                  value={(field.value as string) ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <div className="grid grid-cols-2 gap-4">
        {showIdentity && (
          <FormField
            control={control}
            name={fieldName('birthDate')}
            render={({ field, fieldState }) => (
              <div className={cn(!showHealth && 'col-span-2')}>
                <PrefillDateField
                  id={field.name}
                  label="Date of birth"
                  value={field.value as Date | undefined}
                  error={fieldState.error?.message}
                  onChange={field.onChange}
                  required
                />
              </div>
            )}
          />
        )}

        {showHealth && (
          <FormField
            control={control}
            name={fieldName('sex')}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Sex <span className="text-destructive">*</span>
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value as string}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select sex" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="z-99">
                    {SEX_OPTIONS.map(option => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      {showHealth && (
        <FormField
          control={control}
          name={fieldName('smokingStatus')}
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>
                Smoking status <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value as string}
                  className="flex gap-4"
                >
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="never" />
                    </FormControl>
                    <FormLabel className="font-normal">Never</FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="former" />
                    </FormControl>
                    <FormLabel className="font-normal">Former</FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="current" />
                    </FormControl>
                    <FormLabel className="font-normal">Current</FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {showHealth && isWizardSession && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor={autofillId}>Autofill zip code by state (admin only)</Label>
            <Select
              value={autofillState}
              onValueChange={abbr =>
                setValue(fieldName('zipCode') as string, STATE_ZIP_CODES[abbr], {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
            >
              <SelectTrigger
                id={autofillId}
                className="w-full"
              >
                <SelectValue placeholder="Select a state" />
              </SelectTrigger>
              <SelectContent className="z-99">
                {STATE_AUTOFILL_OPTIONS.map(({ abbr, name }) => (
                  <SelectItem
                    key={abbr}
                    value={abbr}
                  >
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {showHealth && (
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name={fieldName('zipCode')}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Zip code <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="12345"
                    maxLength={5}
                    {...field}
                    value={field.value as string}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name={fieldName('maritalStatus')}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Marital status <span className="text-destructive">*</span>
                </FormLabel>
                <Select
                  onValueChange={onMaritalStatusChange ?? field.onChange}
                  value={field.value as string}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="z-99">
                    {MARITAL_STATUS_OPTIONS.map(option => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        disabled={
                          fieldPrefix === 'partner.' &&
                          !PARTNER_ELIGIBLE_STATUSES.includes(option.value)
                        }
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
}
